import React, { useState, useMemo, useCallback, useEffect } from "react";
import {
  GoogleMap,
  useJsApiLoader,
  MarkerF,
  InfoWindowF,
} from "@react-google-maps/api";
import { useNavigate } from "react-router-dom";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { getGoogleMapsApiKey } from "../../config/googleMapsConfig";
// import { getLocalLogoPath } from "../../lib/logoResolver";

// Define libraries outside component to prevent re-creation
const mapLibraries = ["places"];

// Map container styling
const containerStyle = {
  width: "100%",
  height: "600px",
  borderRadius: "0.75rem",
};

// Default map center (centered on US, but will auto-adjust to markers)
const defaultCenter = {
  lat: 39.8283,
  lng: -98.5795,
};

// Clean, minimal map styling with brand colors
// STRATEGY:
// - Light green land (#C7E5CE) - brand color, natural look
// - Light blue water (#C5E9F9) - brand color, clear water appearance
// - Purple borders for geographic clarity
// - Indigo vendor markers
// - Clean and professional
const mapStyles = [
  // LAND - Light green (brand color)
  {
    featureType: "landscape",
    elementType: "geometry",
    stylers: [{ color: "#C7E5CE" }], // Lightest green - brand color
  },
  {
    featureType: "landscape.natural",
    elementType: "geometry",
    stylers: [{ color: "#C7E5CE" }], // Match base for consistency
  },
  {
    featureType: "landscape.man_made",
    elementType: "geometry",
    stylers: [{ color: "#C7E5CE" }], // Match base
  },

  // WATER - Light blue (brand color)
  {
    featureType: "water",
    elementType: "geometry",
    stylers: [{ color: "#C5E9F9" }], // Lightest blue - brand color
  },
  {
    featureType: "water",
    elementType: "labels.text.fill",
    stylers: [{ color: "#3A82F4" }], // Brighter blue for water labels (brand color)
  },
  {
    featureType: "water",
    elementType: "labels.text.stroke",
    stylers: [{ color: "#FFFFFF" }, { weight: 2 }],
  },

  // PARKS - Match land for consistency
  {
    featureType: "poi.park",
    elementType: "geometry",
    stylers: [{ color: "#C7E5CE" }], // Match land color
  },
  {
    featureType: "poi.park",
    elementType: "labels.text.fill",
    stylers: [{ color: "#0ACC59" }], // Brighter green for park labels (brand color)
  },

  // BORDERS - Purple (PRIMARY VISUAL ELEMENT for geography)
  {
    featureType: "administrative.country",
    elementType: "geometry.stroke",
    stylers: [
      { color: "#6036C4" }, // PRIMARY PURPLE - country borders
      { weight: 1.0 },
    ],
  },
  {
    featureType: "administrative.province",
    elementType: "geometry.stroke",
    stylers: [
      { color: "#A68FF4" }, // Medium lavender - state borders
      { weight: 0.8 },
    ],
  },
  {
    featureType: "administrative.locality",
    elementType: "geometry.stroke",
    stylers: [
      { color: "#E6E4F2" }, // Very light lavender - subtle
      { weight: 0.3 },
    ],
  },

  // ROADS - Minimal grey
  {
    featureType: "road",
    elementType: "geometry.fill",
    stylers: [{ color: "#FFFFFF" }], // White
  },
  {
    featureType: "road",
    elementType: "geometry.stroke",
    stylers: [{ color: "#E5E7EB" }, { weight: 0.3 }], // Very subtle grey
  },
  {
    featureType: "road.highway",
    elementType: "geometry.fill",
    stylers: [{ color: "#FFFFFF" }],
  },
  {
    featureType: "road.highway",
    elementType: "geometry.stroke",
    stylers: [
      { color: "#CFC6EF" }, // Light purple for highways
      { weight: 0.8 },
    ],
  },

  // TEXT LABELS
  {
    featureType: "all",
    elementType: "labels.text.fill",
    stylers: [{ color: "#374151" }], // Grey-700 for readability
  },
  {
    featureType: "all",
    elementType: "labels.text.stroke",
    stylers: [{ color: "#FFFFFF" }, { weight: 3 }],
  },

  // POINTS OF INTEREST - De-emphasize
  {
    featureType: "poi",
    elementType: "geometry",
    stylers: [{ color: "#C7E5CE" }], // Match land
  },
  {
    featureType: "poi",
    elementType: "labels.text.fill",
    stylers: [{ color: "#44289E" }], // Purple for consistency
  },
  {
    featureType: "poi",
    elementType: "labels.icon",
    stylers: [{ visibility: "off" }],
  },
  {
    featureType: "poi.business",
    stylers: [{ visibility: "off" }],
  },

  // TRANSIT - Minimal
  {
    featureType: "transit",
    elementType: "geometry",
    stylers: [{ color: "#C7E5CE" }], // Match land
  },
  {
    featureType: "transit.station",
    elementType: "labels.icon",
    stylers: [{ visibility: "off" }],
  },
];

// Geocoding cache to avoid repeated API calls
const geocodeCache = new Map();

/**
 * ProvidersMapView - Displays vendors on an interactive map
 * Integrates with existing filter system from Providers.jsx
 */
export default function ProvidersMapView({
  providers = [],
  selectedOptions = [],
  setSelectedOptions,
  serviceColors,
}) {
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [map, setMap] = useState(null);
  const navigate = useNavigate();

  // Get Google Maps API key (uses existing key from location autocomplete as fallback)
  const apiKey = getGoogleMapsApiKey();

  // Use the proper hook to load Google Maps
  const { isLoaded, loadError } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: apiKey,
    libraries: mapLibraries,
  });

  // Process vendors to extract geographic coordinates
  const [resolvedLogos, setResolvedLogos] = useState({});

  // Resolve logos for all providers
  useEffect(() => {
    const resolveLogos = async () => {
      const logos = {};
      for (const provider of providers) {
        if (provider.name) {
          try {
            // const logoPath = await getLocalLogoPath(provider.name);
            const logoPath = provider.logo;
            if (logoPath) {
              logos[provider.name] = logoPath;
            }
          } catch (e) {
            // If resolution fails, try provider.logo or fallback
            logos[provider.name] = provider.logo || null;
          }
        }
      }
      setResolvedLogos(logos);
    };

    resolveLogos();
  }, [providers]);

  const geocodedProviders = useMemo(() => {
    return providers
      .map((provider) =>
        (provider.locations || [provider])?.map((loc) => ({
          ...provider,
          country: loc.country,
          state: loc.state,
          city: loc.city,
        }))
      )
      .flat()
      .map((provider) => {
        // Try to get location from various fields
        const country = provider.primaryCountry || provider.country || "";
        const city = provider.city || "";
        const state = provider.state || "";
        const cityState =
          provider.primaryCityState ||
          [city || "", state || ""].filter((s) => s).join(", ") ||
          "";

        // Skip if no location data
        if (!country && !cityState && !city) {
          return null;
        }

        // Build location string for geocoding
        const locationParts = [];
        if (cityState) locationParts.push(cityState);
        else if (city) locationParts.push(city);
        if (state && !cityState) locationParts.push(state);
        if (country) locationParts.push(country);

        const locationString = locationParts.join(", ");

        // Simple geocoding approximation (in production, you'd use Google Geocoding API)
        // For now, we'll use approximate coordinates for major countries
        const coords = getApproximateCoordinates(country, city, cityState);

        if (!coords) return null;

        // Get resolved logo path
        const logo = resolvedLogos[provider.name] || provider.logo;

        return {
          ...provider,
          location: locationString,
          coordinates: coords,
          logo: logo, // Use properly resolved logo path
        };
      })
      .filter(Boolean);
  }, [providers, resolvedLogos]);

  const plottedProviders = useMemo(
    () => Array.from(new Set(geocodedProviders.map((p) => p.id))),
    [geocodedProviders]
  );

  // Calculate map bounds to fit all markers
  const mapBounds = useMemo(() => {
    if (geocodedProviders.length === 0) return null;

    const bounds = {
      north: -90,
      south: 90,
      east: -180,
      west: 180,
    };

    geocodedProviders.forEach((provider) => {
      const { lat, lng } = provider.coordinates;
      bounds.north = Math.max(bounds.north, lat);
      bounds.south = Math.min(bounds.south, lat);
      bounds.east = Math.max(bounds.east, lng);
      bounds.west = Math.min(bounds.west, lng);
    });

    return bounds;
  }, [geocodedProviders]);

  // Auto-fit map to markers on load
  const onMapLoad = useCallback(
    (mapInstance) => {
      setMap(mapInstance);
      if (mapBounds && geocodedProviders.length > 0) {
        mapInstance.fitBounds(mapBounds);
      }
    },
    [mapBounds, geocodedProviders]
  );

  // Handle map click to close info window
  const handleMapClick = useCallback(() => {
    setSelectedMarker(null);
  }, []);

  // Handle marker click
  const handleMarkerClick = useCallback((provider) => {
    setSelectedMarker(provider);
  }, []);

  // Handle vendor selection (for comparison)
  const handleVendorSelect = useCallback(
    (provider) => {
      const isSelected = selectedOptions.some((v) => v.name === provider.name);
      if (isSelected) {
        setSelectedOptions(
          selectedOptions.filter((v) => v.name !== provider.name)
        );
      } else {
        setSelectedOptions([...selectedOptions, provider]);
      }
    },
    [selectedOptions, setSelectedOptions]
  );

  // Navigate to vendor details
  const handleViewDetails = useCallback(
    (provider) => {
      navigate(`/Provider/${provider.id}/overview`);
    },
    [navigate]
  );

  // Check if vendor is selected
  const isVendorSelected = useCallback(
    (provider) => {
      return selectedOptions.some((v) => v.name === provider.name);
    },
    [selectedOptions]
  );

  // Show error if map failed to load
  if (loadError) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <h3 className="text-sm font-semibold text-gray-900">Map View</h3>
        </div>
        <div className="p-8">
          <div className="text-center">
            <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-red-500" />
            <h3 className="mt-4 text-lg font-semibold text-gray-900">
              Failed to Load Google Maps
            </h3>
            <p className="mt-2 text-sm text-gray-600 max-w-2xl mx-auto">
              {loadError.message ||
                "There was an error loading the map. Please check your API key and network connection."}
            </p>
            <div className="mt-6 bg-gray-50 rounded-lg p-4 text-left max-w-2xl mx-auto">
              <p className="text-xs font-semibold text-gray-700 mb-2">
                Common Solutions:
              </p>
              <ul className="text-xs text-gray-600 space-y-1 list-disc list-inside">
                <li>
                  Verify Maps JavaScript API is enabled in Google Cloud Console
                </li>
                <li>
                  Check if billing is enabled (required even for free tier)
                </li>
                <li>Verify API key restrictions allow localhost</li>
                <li>Check browser console for detailed error messages</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show loading state
  if (!isLoaded) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <h3 className="text-sm font-semibold text-gray-900">Map View</h3>
        </div>
        <div className="p-8">
          <div
            className="flex items-center justify-center"
            style={{ height: "600px" }}
          >
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
              <p className="mt-4 text-sm text-gray-600">Loading map...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      {/* Map Statistics */}
      <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Map View</h3>
            <p className="text-xs text-gray-500 mt-1">
              Showing {plottedProviders.length} of {providers.length} vendors
              with location data
            </p>
          </div>
          {providers.length > plottedProviders.length && (
            <div className="text-xs text-gray-500">
              {providers.length - plottedProviders.length} vendors without
              location data
            </div>
          )}
        </div>
      </div>

      {/* Google Map */}
      <div className="p-4">
        <GoogleMap
          mapContainerStyle={containerStyle}
          center={defaultCenter}
          zoom={4}
          onLoad={onMapLoad}
          onClick={handleMapClick}
          options={{
            styles: mapStyles,
            zoomControl: true,
            zoomControlOptions: {
              position: 9, // RIGHT_TOP
              style: 1, // SMALL (minimal zoom buttons)
            },
            streetViewControl: false,
            mapTypeControl: false,
            fullscreenControl: true,
            fullscreenControlOptions: {
              position: 9, // RIGHT_TOP
            },
            // Clean, minimal UI
            gestureHandling: "greedy",
            clickableIcons: false, // Disable clickable POI icons
            disableDefaultUI: false, // Keep essential controls
            backgroundColor: "#C7E5CE", // Light green to match land
          }}
        >
          {/* Render markers for each vendor */}
          {geocodedProviders?.map((provider, index) => {
            const isSelected = isVendorSelected(provider);
            return (
              <MarkerF
                key={`${provider.id}-${index}`}
                position={provider.coordinates}
                onClick={() => handleMarkerClick(provider)}
                title={provider.name}
                icon={{
                  path: window.google?.maps?.SymbolPath?.CIRCLE || 0,
                  scale: 9,
                  fillColor: isSelected ? "#4F46E5" : "#6366F1", // Indigo-600 (selected) / Indigo-500
                  fillOpacity: isSelected ? 1.0 : 0.85,
                  strokeColor: "#FFFFFF", // White
                  strokeWeight: 2.5,
                }}
                animation={null}
                zIndex={isSelected ? 1000 : 1} // Selected markers appear on top
              />
            );
          })}

          {/* Info Window for selected marker */}
          {selectedMarker && (
            <InfoWindowF
              position={selectedMarker.coordinates}
              onCloseClick={() => setSelectedMarker(null)}
              options={{
                disableAutoPan: false,
                maxWidth: 320,
                pixelOffset: new window.google.maps.Size(0, -10),
              }}
            >
              <div className="p-3 max-w-sm" style={{ minWidth: "280px" }}>
                {/* Header with logo and name */}
                <div className="flex items-start gap-3 mb-3">
                  {/* Vendor Logo - styled like profile page */}
                  <div className="flex-shrink-0 w-16 h-16 rounded-lg bg-gray-100 p-2.5 flex items-center justify-center overflow-hidden">
                    {selectedMarker.logo ? (
                      <img
                        src={selectedMarker.logo}
                        alt={`${selectedMarker.name} logo`}
                        className="w-full h-full object-contain"
                        style={{ maxWidth: "100%", maxHeight: "100%" }}
                        onError={(e) => {
                          // Fallback to initials if logo fails
                          e.target.style.display = "none";
                          const parent = e.target.parentElement;
                          parent.innerHTML = `<span class="text-gray-400 text-sm font-bold">${selectedMarker.name
                            .substring(0, 2)
                            .toUpperCase()}</span>`;
                        }}
                      />
                    ) : (
                      <span className="text-gray-400 text-sm font-bold">
                        {selectedMarker.name.substring(0, 2).toUpperCase()}
                      </span>
                    )}
                  </div>

                  {/* Name, Type, and Location */}
                  <div className="flex-1 min-w-0">
                    <h4
                      className="text-sm font-semibold text-gray-900 mb-1 truncate"
                      title={selectedMarker.name}
                    >
                      {selectedMarker.name}
                    </h4>
                    {selectedMarker.provider_type && (
                      <p className="text-xs text-gray-600 mb-1">
                        {selectedMarker.provider_type}
                      </p>
                    )}
                    {/* Location directly below type */}
                    <p className="text-xs text-gray-500 flex items-start">
                      <svg
                        className="w-3 h-3 mr-1 mt-0.5 flex-shrink-0"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                      </svg>
                      <span className="flex-1">{selectedMarker.location}</span>
                    </p>
                  </div>
                </div>

                {/* Service Categories */}
                {selectedMarker.provider_capabilities &&
                  selectedMarker.provider_capabilities.length > 0 && (
                    <div className="mb-3">
                      <p className="text-xs font-medium text-gray-700 mb-1.5">
                        Services
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        {selectedMarker.provider_capabilities
                          ?.slice(0, 4)
                          ?.map((service, idx) => {
                            const colors = serviceColors[service.rfi_name] || {
                              bgColor: "bg-gray-50",
                              textColor: "text-gray-700",
                              borderColor: "ring-gray-200",
                            };
                            const colorStyle = colors
                              ? {
                                  backgroundColor: colors.bgColor,
                                  color: colors.textColor,
                                  boxShadow: `inset 0 0 0 1px ${colors.borderColor}1A`,
                                }
                              : {};
                            return (
                              <span
                                key={idx}
                                style={colorStyle}
                                className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset ${colors.bgColor} ${colors.textColor} ${colors.borderColor}`}
                              >
                                {service.rfi_name}
                              </span>
                            );
                          })}
                        {selectedMarker.provider_capabilities.length > 4 && (
                          <span className="inline-flex items-center px-2 py-1 text-xs text-gray-500 font-medium">
                            +{selectedMarker.provider_capabilities.length - 4}
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                {/* Action Buttons */}
                <div className="flex gap-2 pt-2 border-t border-gray-100">
                  <button
                    onClick={() => handleViewDetails(selectedMarker)}
                    className="flex-1 inline-flex items-center justify-center rounded-md bg-indigo-600 px-3 py-2 text-xs font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 transition-colors"
                  >
                    View Profile
                  </button>
                  <button
                    onClick={() => handleVendorSelect(selectedMarker)}
                    className={`flex-1 inline-flex items-center justify-center rounded-md px-3 py-2 text-xs font-semibold shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 transition-colors ${
                      isVendorSelected(selectedMarker)
                        ? "bg-gray-600 text-white hover:bg-gray-500 focus-visible:outline-gray-600"
                        : "bg-white text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                    }`}
                  >
                    {isVendorSelected(selectedMarker) ? "Deselect" : "Select"}
                  </button>
                </div>
              </div>
            </InfoWindowF>
          )}
        </GoogleMap>
      </div>

      {/* Legend */}
      <div className="px-6 py-3 bg-gradient-to-r from-gray-50 to-indigo-50/30 border-t border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-6 text-xs text-gray-600">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-indigo-500 opacity-75 border-2 border-white shadow-sm"></div>
              <span className="font-medium">Available</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-indigo-600 border-2 border-white shadow-sm"></div>
              <span className="font-medium">Selected</span>
            </div>
          </div>
          <div className="text-xs text-gray-500 italic">
            Click markers to view details
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Get approximate coordinates for a location
 * In production, this would use Google Geocoding API
 * For now, using approximate coordinates for major locations
 */
function getApproximateCoordinates(country, city, cityState) {
  const countryNorm = (country || "").toLowerCase().trim();
  const cityNorm = (city || "").toLowerCase().trim();
  const cityStateNorm = (cityState || "").toLowerCase();

  // Add some randomization to prevent markers from stacking exactly on top of each other
  const jitter = () => (Math.random() - 0.5) * 0.5;

  // US State abbreviations and names mapping
  const usStates = {
    al: { name: "alabama", lat: 32.806671, lng: -86.79113 },
    ak: { name: "alaska", lat: 61.370716, lng: -152.404419 },
    az: { name: "arizona", lat: 33.729759, lng: -111.431221 },
    ar: { name: "arkansas", lat: 34.969704, lng: -92.373123 },
    ca: { name: "california", lat: 36.778259, lng: -119.417931 },
    co: { name: "colorado", lat: 39.059811, lng: -105.311104 },
    ct: { name: "connecticut", lat: 41.597782, lng: -72.755371 },
    de: { name: "delaware", lat: 39.318523, lng: -75.507141 },
    fl: { name: "florida", lat: 27.994402, lng: -81.760254 },
    ga: { name: "georgia", lat: 33.040619, lng: -83.643074 },
    hi: { name: "hawaii", lat: 21.094318, lng: -157.498337 },
    id: { name: "idaho", lat: 44.240459, lng: -114.478828 },
    il: { name: "illinois", lat: 40.349457, lng: -88.986137 },
    in: { name: "indiana", lat: 39.849426, lng: -86.258278 },
    ia: { name: "iowa", lat: 42.011539, lng: -93.210526 },
    ks: { name: "kansas", lat: 38.5266, lng: -96.726486 },
    ky: { name: "kentucky", lat: 37.66814, lng: -84.670067 },
    la: { name: "louisiana", lat: 31.169546, lng: -91.867805 },
    me: { name: "maine", lat: 44.693947, lng: -69.381927 },
    md: { name: "maryland", lat: 39.063946, lng: -76.802101 },
    ma: { name: "massachusetts", lat: 42.230171, lng: -71.530106 },
    mi: { name: "michigan", lat: 43.326618, lng: -84.536095 },
    mn: { name: "minnesota", lat: 45.694454, lng: -93.900192 },
    ms: { name: "mississippi", lat: 32.741646, lng: -89.678696 },
    mo: { name: "missouri", lat: 38.456085, lng: -92.288368 },
    mt: { name: "montana", lat: 46.921925, lng: -110.454353 },
    ne: { name: "nebraska", lat: 41.12537, lng: -98.268082 },
    nv: { name: "nevada", lat: 38.313515, lng: -117.055374 },
    nh: { name: "new hampshire", lat: 43.452492, lng: -71.563896 },
    nj: { name: "new jersey", lat: 40.298904, lng: -74.521011 },
    nm: { name: "new mexico", lat: 34.840515, lng: -106.248482 },
    ny: { name: "new york", lat: 42.165726, lng: -74.948051 },
    nc: { name: "north carolina", lat: 35.630066, lng: -79.806419 },
    nd: { name: "north dakota", lat: 47.528912, lng: -99.784012 },
    oh: { name: "ohio", lat: 40.388783, lng: -82.764915 },
    ok: { name: "oklahoma", lat: 35.565342, lng: -96.928917 },
    or: { name: "oregon", lat: 44.572021, lng: -122.070938 },
    pa: { name: "pennsylvania", lat: 40.590752, lng: -77.209755 },
    ri: { name: "rhode island", lat: 41.680893, lng: -71.51178 },
    sc: { name: "south carolina", lat: 33.856892, lng: -80.945007 },
    sd: { name: "south dakota", lat: 44.299782, lng: -99.438828 },
    tn: { name: "tennessee", lat: 35.747845, lng: -86.692345 },
    tx: { name: "texas", lat: 31.054487, lng: -97.563461 },
    ut: { name: "utah", lat: 40.150032, lng: -111.862434 },
    vt: { name: "vermont", lat: 44.045876, lng: -72.710686 },
    va: { name: "virginia", lat: 37.769337, lng: -78.169968 },
    wa: { name: "washington", lat: 47.400902, lng: -121.490494 },
    wv: { name: "west virginia", lat: 38.491226, lng: -80.954453 },
    wi: { name: "wisconsin", lat: 44.268543, lng: -89.616508 },
    wy: { name: "wyoming", lat: 42.755966, lng: -107.30249 },
    dc: { name: "district of columbia", lat: 38.907192, lng: -77.036871 },
  };

  // Major countries and cities approximations
  const locationMap = {
    // United States
    "united states": { lat: 37.0902 + jitter(), lng: -95.7129 + jitter() },
    usa: { lat: 37.0902 + jitter(), lng: -95.7129 + jitter() },
    us: { lat: 37.0902 + jitter(), lng: -95.7129 + jitter() },

    // Canada
    canada: { lat: 56.1304 + jitter(), lng: -106.3468 + jitter() },

    // United Kingdom
    "united kingdom": { lat: 55.3781 + jitter(), lng: -3.436 + jitter() },
    uk: { lat: 55.3781 + jitter(), lng: -3.436 + jitter() },

    // European countries
    germany: { lat: 51.1657 + jitter(), lng: 10.4515 + jitter() },
    france: { lat: 46.2276 + jitter(), lng: 2.2137 + jitter() },
    italy: { lat: 41.8719 + jitter(), lng: 12.5674 + jitter() },
    spain: { lat: 40.4637 + jitter(), lng: -3.7492 + jitter() },
    netherlands: { lat: 52.1326 + jitter(), lng: 5.2913 + jitter() },
    belgium: { lat: 50.5039 + jitter(), lng: 4.4699 + jitter() },
    switzerland: { lat: 46.8182 + jitter(), lng: 8.2275 + jitter() },
    austria: { lat: 47.5162 + jitter(), lng: 14.5501 + jitter() },
    sweden: { lat: 60.1282 + jitter(), lng: 18.6435 + jitter() },
    norway: { lat: 60.472 + jitter(), lng: 8.4689 + jitter() },
    denmark: { lat: 56.2639 + jitter(), lng: 9.5018 + jitter() },
    finland: { lat: 61.9241 + jitter(), lng: 25.7482 + jitter() },
    poland: { lat: 51.9194 + jitter(), lng: 19.1451 + jitter() },
    ireland: { lat: 53.4129 + jitter(), lng: -8.2439 + jitter() },

    // Asian countries
    china: { lat: 35.8617 + jitter(), lng: 104.1954 + jitter() },
    japan: { lat: 36.2048 + jitter(), lng: 138.2529 + jitter() },
    india: { lat: 20.5937 + jitter(), lng: 78.9629 + jitter() },
    "south korea": { lat: 35.9078 + jitter(), lng: 127.7669 + jitter() },
    singapore: { lat: 1.3521 + jitter(), lng: 103.8198 + jitter() },
    australia: { lat: -25.2744 + jitter(), lng: 133.7751 + jitter() },
    "new zealand": { lat: -40.9006 + jitter(), lng: 174.886 + jitter() },

    // Other regions
    brazil: { lat: -14.235 + jitter(), lng: -51.9253 + jitter() },
    mexico: { lat: 23.6345 + jitter(), lng: -102.5528 + jitter() },
    argentina: { lat: -38.4161 + jitter(), lng: -63.6167 + jitter() },
    "south africa": { lat: -30.5595 + jitter(), lng: 22.9375 + jitter() },
    israel: { lat: 31.0461 + jitter(), lng: 34.8516 + jitter() },
  };

  // Check if this is US with state information
  if (
    (countryNorm === "united states" ||
      countryNorm === "usa" ||
      countryNorm === "us") &&
    cityStateNorm
  ) {
    // Try to extract state abbreviation or name from cityState
    // Format could be "City, ST" or "City, State Name"
    const parts = cityStateNorm.split(",")?.map((p) => p.trim());
    if (parts.length >= 2) {
      const statePart = parts[parts.length - 1]; // Last part is usually state

      // Check if it's a 2-letter abbreviation
      if (statePart.length === 2 && usStates[statePart]) {
        const state = usStates[statePart];
        return { lat: state.lat + jitter(), lng: state.lng + jitter() };
      }

      // Check if it matches a full state name
      const stateEntry = Object.values(usStates).find(
        (s) => s.name === statePart
      );
      if (stateEntry) {
        return {
          lat: stateEntry.lat + jitter(),
          lng: stateEntry.lng + jitter(),
        };
      }

      // Try matching partial state name
      for (const [abbr, stateData] of Object.entries(usStates)) {
        if (
          statePart.includes(stateData.name) ||
          stateData.name.includes(statePart)
        ) {
          return {
            lat: stateData.lat + jitter(),
            lng: stateData.lng + jitter(),
          };
        }
      }
    }
  }

  // Try to match country
  if (countryNorm && locationMap[countryNorm]) {
    return locationMap[countryNorm];
  }

  // Default fallback - return null to skip this vendor
  return null;
}
